import React, { useState } from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import WebSocketFooter from '../shared/WebSocketFooter';
import { TrendingUp, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getProblems = async () => {
  const response = await fetch(`${BACKEND_URL}/api/problems`);
  if (!response.ok) throw new Error('Failed to fetch problems');
  return response.json();
};

const getProblemStats = async () => {
  const response = await fetch(`${BACKEND_URL}/api/problems/stats`);
  if (!response.ok) throw new Error('Failed to fetch problem stats');
  return response.json();
};

const ProblemsPage = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const { data: problems, loading: loadingProblems, error: errorProblems, refetch: refetchProblems } = useDataFetching(getProblems, []);
  const { data: stats, loading: loadingStats, error: errorStats, refetch: refetchStats } = useDataFetching(getProblemStats, []);

  if (loadingProblems || loadingStats) return <LoadingState message="Loading problems..." />;
  if (errorProblems || errorStats) return <ErrorState message={errorProblems || errorStats} onRetry={() => { refetchProblems(); refetchStats(); }} />;

  const clearFilters = () => {
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  const getPriorityBadge = (priority) => {
    if (priority.includes('Critical')) return { bg: 'bg-red-100', text: 'text-red-700', label: 'CRITICAL' };
    if (priority.includes('High')) return { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH' };
    return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'MEDIUM' };
  };

  const getStateBadge = (state) => {
    if (state === 'Root Cause Analysis') return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'RCA' };
    if (state === 'In Progress') return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'New' };
    return { bg: 'bg-gray-100', text: 'text-gray-700', label: state };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Problem Management</h1>
          <p className="text-gray-600 mt-1">Root Cause Analysis and Pattern Detection for Systematic Issues</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-5 w-5 text-gray-600" />
              <span className="text-sm text-gray-600">Total Problems</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.total_problems || 28}</div>
            <div className="text-sm text-gray-500 mt-1">{stats?.total_problems || 28} new</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-gray-600" />
              <span className="text-sm text-gray-600">Active Analysis</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats?.in_rca || 0}</div>
            <div className="text-sm text-gray-500 mt-1">In progress</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-gray-600" />
              <span className="text-sm text-gray-600">Root Causes Found</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">0</div>
            <div className="text-sm text-gray-500 mt-1">Identified</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-gray-600" />
              <span className="text-sm text-gray-600">Resolution Rate</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">0%</div>
            <div className="text-sm text-gray-500 mt-1">Avg 0h resolution time</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-sm text-gray-600">🔍 Filters:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="rca">Root Cause Analysis</option>
            <option value="resolved">Resolved</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
          </select>

          <button
            onClick={clearFilters}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            Clear Filters
          </button>
        </div>

        {/* Problems Section */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Problems & Root Cause Analysis</h2>
        </div>

        {/* Problem Cards */}
        <div className="space-y-4">
          {problems && problems.map((problem) => {
            const priorityBadge = getPriorityBadge(problem.priority);
            const stateBadge = getStateBadge(problem.state);
            
            return (
              <div key={problem.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Problem Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <TrendingUp className="h-5 w-5 text-orange-500 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{problem.short_description}</h3>
                        <span className={`px-2 py-1 ${stateBadge.bg} ${stateBadge.text} text-xs font-semibold rounded`}>
                          {stateBadge.label}
                        </span>
                        <span className={`px-2 py-1 ${priorityBadge.bg} ${priorityBadge.text} text-xs font-semibold rounded`}>
                          {priorityBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        {problem.state === 'Root Cause Analysis' 
                          ? `Progressive analysis underway. Pattern identified across multiple application servers.`
                          : `Recurring timeout issues with external payment processor causing transaction failures during peak hours.`}
                      </p>
                      
                      {/* Problem Metadata */}
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <span>ID: {problem.number}</span>
                        <span>Category: {problem.category}</span>
                        <span>Related Incidents: {problem.related_incidents}</span>
                        <span>Affected Users: {problem.related_incidents * 1000}</span>
                        {problem.state === 'Root Cause Analysis' && <span>Downtime: 60min</span>}
                      </div>

                      {/* Root Cause Section */}
                      <div className="border-t border-gray-200 pt-4">
                        <div className="font-semibold text-gray-900 mb-2">
                          Root Cause: {problem.root_cause}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {problem.category === 'Performance' ? (
                            <>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">memory-leak</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">performance</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">sessions</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">software-bug</span>
                            </>
                          ) : (
                            <>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">payment</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">third-party</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">timeout</span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">peak-hours</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* WebSocket Footer */}
        <WebSocketFooter />
      </div>
    </div>
  );
};

export default ProblemsPage;
